import type { StatusEquipamento } from '@/lib/dominio';

/**
 * Um abastecimento.
 *
 * O que está aqui é o fato bruto do comboio, do tanque do pátio ou do posto:
 * qual máquina, quando, quanto entrou, com que leitura de medidor, de onde saiu
 * o combustível, quem operava, quem liberou e para que obra e centro de custo
 * ele vai.
 *
 * O que NÃO está aqui, porque ainda é decisão em aberto do dono, é o custeio: se
 * o diesel é rateado por obra, por equipamento ou por frente. O `centroCusto`
 * registra o destino do lançamento; ele não decide rateio nenhum.
 *
 * `precoLitro` não é campo de formulário no abastecimento interno: ele vem do
 * tanque (o preço da última compra), e não de quem está na frente de serviço com
 * a mangueira na mão. No abastecimento externo ele é digitado, porque é o preço
 * do cupom — e é contra ele que a cobrança do posto é conferida.
 *
 * ## Por que tantos campos aceitam nulo
 *
 * Porque o banco aceita. `precoLitro` é nulo no interno enquanto o tanque não
 * tiver recebimento com nota; `horimetro` é nulo quando ninguém leu o medidor;
 * `tanque` é nulo no externo e `posto` é nulo no interno — o banco recusa as
 * duas combinações ao contrário. Escrever zero no lugar de "não sei" é a forma
 * mais rápida de um indicador mentir.
 */
export interface Abastecimento {
  readonly id: string;
  readonly equipamentoCodigo: string;
  readonly quando: Date;
  readonly litros: number;
  /** Preço pago por litro, em reais. `null` quando o lançamento não tem preço. */
  readonly precoLitro: number | null;
  /** Leitura do medidor no momento do abastecimento. `null` quando não houve. */
  readonly horimetro: number | null;
  readonly obra: string | null;
  /** De qual tanque ou comboio saiu o diesel. `null` no abastecimento externo. */
  readonly tanque: string | null;
  /** `null` quando ninguém informou quem operava — vira pendência do lançamento. */
  readonly operador: string | null;
  /** Quem liberou o diesel. */
  readonly responsavel: string | null;
  /**
   * `null` quando ninguém informou o centro de custo — vira pendência, do mesmo
   * jeito que `operador`. O formulário PRÉ-PREENCHE a partir da obra e deixa
   * limpar: recusar o lançamento porque às 6h ninguém sabia o centro de custo
   * troca um dado faltando por um abastecimento que nunca foi registrado.
   */
  readonly centroCusto: string | null;

  /* ------------------------------------------------------------------------
   * O que só existe quando a linha veio do banco.
   *
   * São opcionais porque a massa de demonstração é anterior a eles e continua
   * válida — e porque o campo ausente diz a verdade: aquela linha não passou
   * pelas views do módulo 08.
   * --------------------------------------------------------------------- */

  readonly equipamentoId?: string;
  readonly tipo?: 'interno' | 'externo';
  readonly posto?: string | null;
  readonly comprovante?: string | null;
  readonly produto?: string;
  /** O id do produto. É por ele que a cadeia de consumo se separa. */
  readonly produtoId?: string | null;
  /**
   * A categoria do produto — `combustivel`, `arla`, `lubrificante`, `outro`.
   *
   * Quem a lê é a média de consumo: L/h é de combustível. ARLA tem a própria
   * cadeia (uma leitura de medidor até a seguinte, entre dois abastecimentos
   * DELE), e somá-la à do diesel põe litros errados no numerador e conta as
   * mesmas horas duas vezes no denominador.
   */
  readonly produtoCategoria?: string;
  readonly ordemNumero?: number | null;
  /** O total que o BANCO calculou (coluna gerada). Não se recalcula na tela. */
  readonly valorTotal?: number | null;
  /** O consumo que `comb.consumo_abastecimento` respondeu. */
  readonly consumoDoBanco?: number | null;
  /** Por que o consumo não é confiável, na palavra do próprio banco. */
  readonly limitacao?: string | null;
  /**
   * Se o consumo ficou fora da faixa esperada — apurado no SERVIDOR.
   *
   * Antes isto era calculado no navegador, sobre a lista já recortada nos 200
   * mais recentes. Três consequências: o desvio sumia junto com o recorte,
   * ninguém conseguia perguntar "quais desvios houve no mês" pela API, e a
   * regra valia só para quem abrisse aquela tela. Regra que mora só na tela
   * não é regra.
   *
   * `null` é "não sei", e não "está dentro". Ver `foraDaFaixa` em `api.ts`.
   */
  readonly foraDaFaixa?: boolean | null;
  /**
   * Os litros que entraram na conta do consumo — do intervalo INTEIRO entre
   * duas leituras, e não só os deste lançamento.
   *
   * A diferença entre os dois números não é acadêmica: um abastecimento sem
   * leitura de medidor no meio do caminho queimou combustível que aparece nas
   * horas do intervalo seguinte. Usar só `litros` na média ponderada foi o que
   * fez a tela mostrar 1,0 L/h onde a máquina fazia 4,0.
   */
  readonly litrosNoIntervalo?: number | null;
  /** Quantos lançamentos entraram nesse intervalo sem leitura própria. */
  readonly abastecimentosSemLeitura?: number | null;
  /** A variação do medidor entre as duas leituras, na conta do banco. */
  readonly variacaoDoMedidor?: number | null;
  /**
   * `horimetro` ou `odometro`: o medidor da LEITURA deste lançamento, e com ele
   * a unidade do consumo — hora ou quilômetro. `null` quando não houve leitura.
   *
   * É ele que separa as médias: a variação de um caminhão é km, a de uma
   * escavadeira é h, e somar as duas no mesmo denominador produz um "L/h" que
   * não é o consumo de nada.
   */
  readonly tipoMedidor?: string | null;
  /**
   * 0087 · os ids do que foi lançado, para a CORREÇÃO abrir com o lançamento
   * certo já preenchido: a correção muda litros, data, medidor e cupom, e
   * mantém a máquina, o tanque ou posto, a obra e as pessoas.
   */
  readonly ids?: IdsDoLancamento;
  /** 0087 · o estorno, quando houve: quando, por que e quem. */
  readonly estorno?: EstornoDoAbastecimento | null;
  /** 0087 · o abastecimento estornado que este lançamento corrige. */
  readonly corrigeId?: string | null;
  /** 0087 · o lançamento novo que corrigiu este, quando ele foi estornado por correção. */
  readonly corrigidoPorId?: string | null;
  /**
   * 0122 · a conferência: alguém olhou este lançamento e assinou embaixo.
   *
   * `null` enquanto ninguém olhou — e é a ausência dela que o indicador "Para
   * conferir" conta. Conferir não aprova, não corrige e não muda número
   * nenhum: o lançamento conferido continua o mesmo no livro.
   */
  readonly conferencia?: ConferenciaDoAbastecimento | null;
  /**
   * 0122 · veio da fila do celular (`true`) ou foi digitado na mesa (`false`).
   *
   * Não é campo novo: é a leitura de `chave_de_envio`, que só existe no
   * lançamento que atravessou a fila (0069). Quem confere precisa saber — o
   * número digitado em pé, na frente de serviço, tem outro risco de digitação
   * do que o número conferido com o cupom na mão.
   */
  readonly doCelular?: boolean;
  /**
   * A máquina por extenso, para a segunda linha do código (27/10/2026): a
   * descrição da unidade ou o fabricante e o modelo, e o tipo dela. Os dois
   * vêm da linha do banco; `null` quando a máquina não os tem.
   */
  readonly equipamentoDescricao?: string | null;
  readonly equipamentoTipo?: string | null;
  /**
   * QUEM REGISTROU o lançamento (27/10/2026), lido da trilha de auditoria — a
   * sessão que gravou a linha. Distinto de `operador` e `responsavel`, que são
   * dados escolhidos por quem lança. Ausente na massa de demonstração.
   */
  readonly registro?: RegistroDoAbastecimento;
  /** Veio da folha de campo lida pela IA (0035). */
  readonly daFolha?: boolean;
}

/** Quem gravou a linha e quando — da trilha, nunca do formulário. */
export interface RegistroDoAbastecimento {
  /** Quando a linha entrou no banco; `null` quando a linha não diz. */
  readonly em: Date | null;
  /** O nome de quem registrou; `null` quando a trilha não tem a inserção. */
  readonly por: string | null;
  /** Os papéis em vigor de quem registrou. */
  readonly perfil: string | null;
}

/** 0122 · quando e por quem o lançamento foi conferido. */
export interface ConferenciaDoAbastecimento {
  readonly em: Date;
  /** `null` quando o usuário que conferiu foi apagado depois (a FK é `set null`). */
  readonly por: string | null;
}

export interface IdsDoLancamento {
  readonly tanqueId: string | null;
  readonly postoId: string | null;
  readonly ordemId: string | null;
  readonly obraId: string | null;
  readonly centroCustoId: string | null;
  readonly operadorId: string | null;
  readonly responsavelId: string | null;
  readonly observacao: string | null;
}

export interface EstornoDoAbastecimento {
  readonly em: Date;
  readonly motivo: string | null;
  readonly por: string | null;
}

/**
 * O custo do lançamento — o do banco quando existe, a multiplicação quando não.
 *
 * `null` é resposta legítima: abastecimento interno de tanque que nunca recebeu
 * nota não tem preço, e inventar zero faria o indicador "Gasto" dizer que o
 * diesel saiu de graça.
 */
export function totalDoAbastecimento(a: Abastecimento): number | null {
  if (a.valorTotal !== undefined && a.valorTotal !== null) return a.valorTotal;
  if (a.precoLitro === null) return null;
  return a.litros * a.precoLitro;
}

/** O gasto de uma lista de abastecimentos e o preço médio do litro nela. */
export interface GastoDosAbastecimentos {
  /** A soma dos totais. Lançamento sem preço fica de fora, e não vira zero. */
  readonly gasto: number;
  /** Os litros dos lançamentos COM preço — o denominador do preço médio. */
  readonly litrosComPreco: number;
  readonly semPreco: number;
  /** Gasto dividido pelos litros com preço. `null` quando nenhum tem preço. */
  readonly precoMedio: number | null;
}

/**
 * O gasto e o preço médio ponderado do litro — as duas metades da fração
 * vindas dos MESMOS lançamentos.
 *
 * O gasto só soma quem tem preço; dividi-lo pelos litros de TODOS punha os
 * litros sem preço no denominador. Medido: três lançamentos de R$ 6,24 a
 * R$ 6,49 o litro, ao lado de oito sem preço, davam "R$ 1,67 por litro na média
 * ponderada" — um preço que ninguém pagou.
 */
export function gastoDosAbastecimentos(abastecimentos: readonly Abastecimento[]): GastoDosAbastecimentos {
  let gasto = 0;
  let litrosComPreco = 0;
  let semPreco = 0;
  for (const abastecimento of abastecimentos) {
    const total = totalDoAbastecimento(abastecimento);
    if (total === null) {
      semPreco += 1;
      continue;
    }
    gasto += total;
    litrosComPreco += abastecimento.litros;
  }
  return {
    gasto,
    litrosComPreco,
    semPreco,
    precoMedio: litrosComPreco > 0 ? gasto / litrosComPreco : null,
  };
}

/**
 * Um tanque ou comboio de onde sai o diesel do abastecimento interno.
 *
 * `precoLitro` é o preço da última compra daquele tanque; é ele que o lançamento
 * herda. Quando a linha vem do banco, o preço sai do último **recebimento** do
 * tanque — não existe "preço do tanque" guardado à parte, porque um número que
 * ninguém atualiza é um número que envelhece calado.
 */
export interface Tanque {
  readonly id: string;
  readonly nome: string;
  readonly precoLitro: number | null;
  /** O que já vem escolhido no formulário. Exatamente um tanque é o padrão. */
  readonly padrao: boolean;
}

/**
 * O tanque com o saldo — a ferramenta 1 inteira, do jeito que a view responde.
 *
 * `saldoCalculado` é o saldo do LIVRO (recebimentos menos saídas);
 * `medicaoObservada` é a régua. A diferença entre os dois é o que a ferramenta
 * existe para expor, e `dentroDaTolerancia` usa a tolerância do próprio tanque —
 * comboio em estrada de terra não mede como tanque fixo de pátio.
 */
export interface SaldoDeTanque {
  readonly id: string;
  readonly codigo: string;
  readonly nome: string;
  readonly tipo: string;
  readonly produtoId: string;
  readonly produto: string;
  readonly padrao: boolean;
  readonly capacidadeL: number;
  readonly saldoCalculadoL: number;
  readonly toleranciaL: number;
  readonly precoLitro: number | null;
  readonly obra: string | null;
  readonly medidoEm: Date | null;
  readonly medicaoObservadaL: number | null;
  readonly medicaoCalculadaL: number | null;
  readonly diferencaL: number | null;
  /** `null` enquanto ninguém mediu: sem régua não há o que comparar. */
  readonly dentroDaTolerancia: boolean | null;
}

/**
 * A ordem de abastecimento — a AUTORIZAÇÃO, que nunca é a entrega.
 *
 * As duas quantidades ficam em campos diferentes de propósito, e a diferença
 * vem calculada ao lado. É o coração da ferramenta 2 e o risco que o documento
 * v6 nomeia: "autorização ser confundida com combustível efetivamente
 * entregue".
 */
export interface Ordem {
  readonly id: string;
  readonly numero: number;
  readonly situacao: 'aberta' | 'atendida' | 'cancelada';
  readonly autorizadaEm: Date;
  readonly validadeEm: Date | null;
  readonly equipamentoId: string;
  readonly equipamentoCodigo: string;
  readonly tanqueId: string;
  readonly tanque: string;
  readonly autorizadoL: number;
  readonly entregueL: number;
  readonly saldoL: number;
  readonly abastecimentos: number;
  readonly autorizadoPor: string | null;
  readonly motivoCancelamento: string | null;
}

/** Uma cobrança do posto, ao lado do que foi lançado no sistema. */
export interface Cobranca {
  readonly id: string;
  readonly documento: string;
  readonly competencia: string | null;
  readonly situacao: 'aberta' | 'conciliada' | 'divergente';
  readonly fornecedor: string;
  readonly posto: string | null;
  readonly valorCobrado: number;
  readonly valorLancado: number;
  readonly diferenca: number;
  readonly abastecimentos: number;
  readonly litros: number;
}

/** Uma aplicação de lubrificante: o fato, não a baixa de estoque. */
export interface AplicacaoDeLubrificante {
  readonly id: string;
  readonly quando: Date;
  readonly equipamentoCodigo: string;
  readonly produto: string;
  readonly quantidade: number;
  /** O preço por litro ou por quilo, quando alguém o informou. */
  readonly precoUnitario?: number | null;
  /** O total, coluna GERADA pelo banco. Não se recalcula na tela. */
  readonly valorTotal?: number | null;
  readonly unidade: string;
  readonly ponto: string | null;
  readonly responsavel: string | null;
  readonly medidor: number | null;
}

/**
 * Uma obra e o centro de custo que ela alimenta.
 *
 * Vive aqui, e não num módulo de obras, porque o módulo de obras ainda não
 * existe como tela. Quando existir, este tipo se muda para lá — o que não muda
 * é o formulário, que já escolhe obra e centro de custo como dois campos
 * separados, porque uma obra pode trocar de centro de custo no meio do contrato.
 */
export interface Obra {
  readonly nome: string;
  readonly centroCusto: string;
}

/**
 * O equipamento como o módulo 08 precisa dele — e só isso.
 *
 * Não é o `Equipamento` do módulo 02: aqui interessam a identidade, onde a
 * máquina está, qual medidor ela usa, qual foi a última leitura e qual é a
 * faixa de consumo do modelo. Manter um tipo próprio é o que deixa as duas
 * telas evoluírem sem se puxar: o módulo 02 é dono da ficha, este módulo é dono
 * do abastecimento.
 */
export interface EquipamentoDoModulo {
  readonly id: string;
  readonly codigo: string;
  readonly tipo: string | null;
  readonly modelo: string | null;
  readonly status: StatusEquipamento | null;
  readonly motivo: string | null;
  readonly obraId: string | null;
  readonly obra: string | null;
  readonly centroCustoId: string | null;
  readonly centroCusto: string | null;
  /** `horimetro` ou `odometro`, herdado da configuração do modelo. */
  readonly tipoMedidor: string;
  readonly ultimaLeitura: number | null;
  /** Quando a última leitura foi feita — o formulário do celular a escreve (M12). */
  readonly ultimaLeituraEm: Date | null;
  /** A faixa esperada do modelo, quando a configuração a declara. */
  readonly faixa: FaixaConsumo | null;
  /**
   * Quantos litros cabem no tanque DA MÁQUINA — não no de origem.
   *
   * Vem resolvida pela herança (`coalesce(unidade, configuração)`, migração
   * 0006), como a faixa e o tipo de medidor. `null` quando nem a máquina nem a
   * configuração dela declaram: aí o sistema não confere volume, e diz isso em
   * vez de supor um limite.
   *
   * Ela já chegava da API e era descartada aqui. Foi por isso que a primeira
   * versão desta tela afirmou, em código e em documento, que "o sistema não
   * sabe a capacidade do tanque da máquina" — sabia, e o dado morria no
   * mapeamento.
   */
  readonly capacidadeTanqueL: number | null;
  /** O que o motor queima, da ficha (0060): `diesel`, `gasolina`, `etanol`… */
  readonly combustivel?: string | null;
  /** O produto do último abastecimento de combustível da máquina. */
  readonly ultimoProdutoId?: string | null;
  /** O código interno e a placa: as etiquetas do seletor da máquina (pedido do dono, 31/10/2026). */
  readonly codigoInterno?: string | null;
  readonly placa?: string | null;
}

/**
 * Faixa de consumo esperada de um tipo de máquina, em litros por hora.
 *
 * Serve a UMA coisa só: avisar, antes de salvar, que o lançamento implica um
 * consumo que não bate com o que aquela máquina costuma fazer. É o único jeito
 * de o erro de digitação — um zero a mais nos litros, um dígito trocado no
 * horímetro — ser pego por quem sabe, em vez de virar média errada três meses
 * depois.
 *
 * O aviso NUNCA bloqueia. Máquina em terreno ruim consome fora da faixa de
 * verdade, e um sistema que se recusa a registrar o que aconteceu ensina a
 * pessoa a lançar número redondo para o sistema aceitar.
 */
export interface FaixaConsumo {
  /**
   * O piso e o teto, e CADA UM pode faltar sozinho.
   *
   * Não é frouxidão de tipo: é o que o banco permite e julga. O check
   * `faixa_da_unidade_coerente` (migração `0011`) só compara os dois quando os
   * dois existem, e `comb.consumo_abastecimento` decide o desvio ponta a ponta
   * — só teto acusa consumo acima dele, só piso acusa abaixo, e `null` sai
   * apenas quando NENHUM dos dois foi cadastrado.
   *
   * Exigir o par aqui fazia a tela jogar fora uma faixa que o servidor tinha:
   * o formulário não avisava nada antes de salvar, e a lista, que lê o veredito
   * do servidor, marcava o mesmo lançamento como fora da faixa logo depois.
   * Uma pergunta, duas respostas.
   */
  readonly minimo: number | null;
  readonly maximo: number | null;
}

/**
 * Monta a faixa a partir do que o servidor mandou — ou `null` quando não veio
 * ponta nenhuma.
 *
 * É UMA FUNÇÃO, e não duas linhas dentro do mapeamento, porque o defeito que
 * ela conserta morava exatamente ali: o mapeamento exigia as duas pontas e
 * jogava fora a faixa pela metade que o banco tinha e ia usar. Dentro de
 * `consultas.ts` nenhum teste a alcançava — o arquivo é de hooks. Regra que
 * ninguém consegue medir é regra que volta a quebrar com a suíte verde.
 */
export function faixaDe(minimo: number | null, maximo: number | null): FaixaConsumo | null {
  if (minimo === null && maximo === null) return null;
  return { minimo, maximo };
}
